import axios, { AxiosInstance } from 'axios';

/**
 * Singleton factory for Axios instances.
 *
 * Two separate instances are maintained:
 * - **primary** (`getInstance`): targets `REACT_APP_API_URL`, used by the main application.
 * - **admin** (`getAdmininstance`): targets `REACT_APP_ADMIN_API_URL`, used for admin-scoped operations.
 *
 * Both instances inject the `X-Tenant` header from `localStorage` so every
 * outgoing request is automatically scoped to the current organisation.
 */
export abstract class HttpClient {
	private static instance: AxiosInstance;
	private static adminInstance: AxiosInstance;

	/**
	 * Builds default headers for the primary API instance.
	 * Sets `app-type: full` and attaches `X-Tenant` when an organisation ID is present.
	 */
	private static getHeaders() {
		const headers: Record<string, string> = {
			'app-type': 'full',
		};
		
		const organizationId = localStorage.getItem('BYOD-organizationId');
		if (organizationId) {
			headers['X-Tenant'] = organizationId;
		}
		
		return headers;
	}

	/**
	 * Returns the singleton Axios instance for the primary API.
	 * Creates it on first call using `REACT_APP_API_URL` as the base URL.
	 * @returns The shared primary {@link AxiosInstance}.
	 */
	public static getInstance(): AxiosInstance {
		if (!HttpClient.instance) {
			HttpClient.instance = axios.create({
				baseURL: process.env.REACT_APP_API_URL,
				timeout: 120000,
				headers: HttpClient.getHeaders(),
			});
		}
		return HttpClient.instance;
	}

	/**
	 * Builds default headers for the admin API instance.
	 * Sets `app-type: secondary` and attaches `X-Tenant` when an organisation ID is present.
	 */
	private static getAdminHeaders() {
		const headers: Record<string, string> = {
			'app-type': 'secondary',
		};
		
		const organizationId = localStorage.getItem('BYOD-organizationId');
		if (organizationId) {
			headers['X-Tenant'] = organizationId;
		}
		
		return headers;
	}

	/**
	 * Returns the singleton Axios instance for the admin API.
	 * Creates it on first call using `REACT_APP_ADMIN_API_URL` as the base URL.
	 * @returns The shared admin {@link AxiosInstance}.
	 */
	public static getAdmininstance(): AxiosInstance {
		if (!HttpClient.adminInstance) {
			HttpClient.adminInstance = axios.create({
				baseURL: process.env.REACT_APP_ADMIN_API_URL,
				timeout: 120000,
				headers: HttpClient.getAdminHeaders(),
			});
		}
		return HttpClient.adminInstance;
	}

}
